/**
 * MONEY OUT, AND WHAT IS STILL OWED.
 *
 * Not in the nav on purpose. An invoice is something you look at about a
 * job or about a customer, and the way here is through the Billed stage on
 * the desk or through the person. That is the whole "capability without
 * button bloat" rule: it exists, it is one click away, it is not a
 * permanent tab competing for attention with the work.
 *
 * Open first, because an open invoice is a question and a paid one is a
 * receipt.
 */

import { db } from "../data/client.ts";
import { esc, money, ago, mount, since } from "../ui/dom.ts";
import { shopStamp } from "../ui/shopclock.ts";
import { METHOD_LABEL, type Method } from "../data/payments.ts";
import { thumbHtml, fillThumbs } from "../ui/carthumb.ts";

type Pay = {
  amount: number; method: Method; received_at: string; voided_at: string | null;
  profiles: { full_name: string | null } | null;
};

type Inv = {
  id: string; invoice_number: string; status: string; total: number; issued_at: string;
  job_id: string | null;
  customers: { id: string; full_name: string } | null;
  payments: Pay[] | null;
  jobs: { vehicles: { id: string; year: number | null; make: string | null;
    model: string | null } | null } | null;
};

/**
 * HOW IT WAS PAID, AND WHEN.
 *
 * This list showed a number and a status pill, so "paid" was a colour
 * and nothing else -- no method, no date, no name. That is the one
 * question a paid invoice gets asked, and answering it meant opening
 * the job. A card paid on the customer's own page has nobody to credit,
 * and saying so is more use than an empty space.
 *
 * Struck-off payments are left out here on purpose: they are not money.
 * The job's Money tab still shows them, with the reason against them.
 */
function paidHow(pays: Pay[] | null): string {
  const real = (pays ?? []).filter((p) => !p.voided_at)
    .sort((a, b) => Date.parse(b.received_at) - Date.parse(a.received_at));
  if (!real.length) return "";
  const last = real[0]!;
  const how = METHOD_LABEL[last.method] ?? last.method;
  const who = last.profiles?.full_name
    ? ` &middot; ${esc(last.profiles.full_name)}`
    : last.method === "card" ? " &middot; paid online" : "";
  const when = shopStamp(last.received_at, false);
  return real.length > 1
    ? `${real.length} payments &middot; last ${esc(how)} ${esc(when)}${who}`
    : `${esc(how)} &middot; ${esc(when)}${who}`;
}

const TABS = [
  { key: "open", label: "Owed", statuses: ["open"] },
  { key: "paid", label: "Paid", statuses: ["paid"] },
  { key: "all", label: "All", statuses: [] as string[] },
];
let tab = "open";

export async function invoicesPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head">
        <a class="back" href="#/">&larr; Desk</a>
        <h1>Invoices</h1><p id="inv-sum">&nbsp;</p></header>
      <div class="tabs wide" id="inv-tabs">${TABS.map((t) =>
        `<button class="tab ${t.key === tab ? "on" : ""}" data-tab="${t.key}">${esc(t.label)}</button>`).join("")}</div>
      <div class="card" id="inv-list"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  document.querySelectorAll<HTMLButtonElement>("#inv-tabs .tab").forEach((b) =>
    b.addEventListener("click", () => { tab = b.dataset["tab"] ?? "open"; void invoicesPage(); }));

  const host = document.getElementById("inv-list");
  if (!host) return;
  try {
    const sb = await db();
    const spec = TABS.find((t) => t.key === tab) ?? TABS[0]!;
    let q = sb.from("invoices")
      .select("id,invoice_number,status,total,issued_at,job_id,customers(id,full_name),"
        + "payments(amount,method,received_at,voided_at,profiles!payments_recorded_by_fkey(full_name)),"
        // The car, so a paid row carries the same picture the Desk and Jobs do.
        + "jobs(vehicles(id,year,make,model))")
      .order("issued_at", { ascending: false }).limit(200);
    if (spec.statuses.length) q = q.in("status", spec.statuses);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as Inv[];

    const owed = rows.filter((r) => r.status === "open").reduce((n, r) => n + Number(r.total ?? 0), 0);
    const sum = document.getElementById("inv-sum");
    if (sum) sum.textContent = tab === "open"
      ? `${money(owed)} outstanding across ${rows.length} invoice${rows.length === 1 ? "" : "s"}.`
      : `${rows.length} invoice${rows.length === 1 ? "" : "s"}.`;

    host.innerHTML = rows.length ? rows.map((r) => `
      <a class="row" href="${r.job_id ? `#/job/${r.job_id}` : `#/customer/${r.customers?.id ?? ""}`}">
        ${r.job_id ? thumbHtml(r.job_id, r.jobs?.vehicles) : ""}
        <div class="row-main">
          <div class="row-title"><span class="num">${esc(r.invoice_number)}</span>
            <span class="pill s-${r.status === "paid" ? "paid" : "estimate"}">${esc(r.status)}</span></div>
          <div class="row-sub">${esc(r.customers?.full_name ?? "No customer")}${
            paidHow(r.payments) ? ` &middot; ${paidHow(r.payments)}` : ""}</div>
        </div>
        <div class="row-side"><div class="amount">${money(r.total)}</div>
          <div class="when">${esc(since(r.issued_at))}</div></div>
      </a>`).join("") : `<div class="empty">Nothing here.</div>`;
    void fillThumbs(host);
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}
